/**
 * AM Product Dimensions Section - JavaScript
 * Following Shopify best practices with separate JS file
 * Handles dynamic interactions and enhanced UX
 */

class AMProductDimensions {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.dimensionsSections = document.querySelectorAll('.dimensions-section');
    
    if (this.dimensionsSections.length > 0) {
      this.setupAnimations();
      this.setupResponsiveObserver();
      this.setupAccessibility();
    }
  }

  /**
   * Setup scroll-triggered animations for better UX
   */
  setupAnimations() {
    // Check if IntersectionObserver is supported
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            
            // Animate table rows with stagger effect
            const tableRows = entry.target.querySelectorAll('.dimensions-table tbody tr');
            tableRows.forEach((row, index) => {
              setTimeout(() => {
                row.style.opacity = '1';
                row.style.transform = 'translateY(0)';
              }, index * 100);
            });
          }
        });
      }, {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px'
      });

      this.dimensionsSections.forEach(section => {
        // Initially hide table rows for animation
        const tableRows = section.querySelectorAll('.dimensions-table tbody tr');
        tableRows.forEach(row => {
          row.style.opacity = '0';
          row.style.transform = 'translateY(20px)';
          row.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
        });

        observer.observe(section);
      });
    }
  }

  /**
   * Setup responsive behavior observer
   */
  setupResponsiveObserver() {
    // Watch for viewport changes to optimize layout
    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(entry => {
          const section = entry.target;
          const containerWidth = entry.contentRect.width;
          
          // Add mobile class for styling adjustments
          if (containerWidth < 750) {
            section.classList.add('is-mobile');
            section.classList.remove('is-desktop');
          } else {
            section.classList.add('is-desktop');
            section.classList.remove('is-mobile');
          }
        });
      });

      this.dimensionsSections.forEach(section => {
        resizeObserver.observe(section);
      });
    }
  }

  /**
   * Enhance accessibility features
   */
  setupAccessibility() {
    this.dimensionsSections.forEach(section => {
      // Add ARIA labels to table for screen readers
      const table = section.querySelector('.dimensions-table');
      if (table) {
        table.setAttribute('role', 'table');
        table.setAttribute('aria-label', 'Product dimensions table');
        
        // Add row/cell semantics
        const headers = table.querySelectorAll('th');
        const cells = table.querySelectorAll('td');
        
        headers.forEach((header, index) => {
          header.setAttribute('scope', 'col');
          header.setAttribute('id', `header-${index}`);
        });
        
        // Link cells to headers
        const rows = table.querySelectorAll('tbody tr');
        rows.forEach((row, rowIndex) => {
          const rowCells = row.querySelectorAll('td');
          rowCells.forEach((cell, cellIndex) => {
            cell.setAttribute('headers', `header-${cellIndex}`);
          });
        });
      }

      // Enhanced button accessibility
      const button = section.querySelector('.dimensions-button');
      if (button) {
        button.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            button.click();
          }
        });
      }
    });
  }

  /**
   * Public method to refresh animations (useful for theme editor)
   */
  refresh() {
    this.setup();
  }
}

// Initialize when script loads
const amProductDimensions = new AMProductDimensions();

// Make available globally for theme editor
window.AMProductDimensions = amProductDimensions;

// Shopify theme editor support
if (window.Shopify && window.Shopify.designMode) {  
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.dimensions-section')) {
      amProductDimensions.refresh();
    }
  });
}
